import { useMemo, useState } from 'react';
import { Box } from '@material-ui/core';
import { Product } from '@setvi/shared/interfaces';

import { RFQProducts } from 'pages/auto-rfq/interfaces';
import { RfqPosition } from 'Services/Query/Auto-RFQ/Types';
import {
  PositionsBody,
  PositionValue
} from 'Services/Mutations/Auto-RFQ/types';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';
import { ManualProps } from 'pages/auto-rfq/pages/details/components/manual-entry';
import {
  AlternatesReplaceSource,
  alternatesAnalytics
} from 'pages/auto-rfq/utils/alternates-analytics';
import { SText } from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { ProductFilter } from 'pages/products/components/ai/products-filters/filter';
import {
  CheckIcon,
  ListContainer,
  ListItem
} from 'pages/products/components/ai/products-filters/styled';
import BaseDialog, { Tabs } from '../base-dialog';
import { Catalog, ManualEntry, ProductMatch } from '../index';

const POSITIONS: RfqPosition[] = ['top', 'alt1', 'alt2'];

export type ChipFilter = 'all' | 'pv' | 'pl';

// The productId currently occupying each position (matched product / alt
// snapshot), used to detect a duplicate pick and turn it into a swap.
const positionProductId = (item: RFQProducts, position: RfqPosition) => {
  if (position === 'top')
    return item?.matchedInternalProducts?.[item?.internalIndex || 0]?._id;
  const slot = position === 'alt1' ? 1 : 2;
  const alt = item?.alternates?.find(a => a.slot === slot);
  return alt?.state === 'filled' ? alt?.productId || undefined : undefined;
};

// Build the full { top, alt1, alt2 } arrangement for a pick at `position`.
// If the picked productId already sits in another filled position, express it
// as a swap (the two positions trade content) — otherwise set it directly.
export const buildPositionsBody = (
  item: RFQProducts,
  position: RfqPosition,
  pick: PositionValue
): PositionsBody => {
  if (pick && 'productId' in pick) {
    const occupant = POSITIONS.find(
      p => p !== position && positionProductId(item, p) === pick.productId
    );
    if (occupant) {
      return POSITIONS.reduce((acc, p) => {
        let keep: RfqPosition = p;
        if (p === position) keep = occupant;
        else if (p === occupant) keep = position;
        acc[p] = { keep } as PositionValue;
        return acc;
      }, {} as PositionsBody);
    }
  }
  return POSITIONS.reduce((acc, p) => {
    acc[p] = p === position ? pick : ({ keep: p } as PositionValue);
    return acc;
  }, {} as PositionsBody);
};

interface PicksTabProps {
  item: RFQProducts;
  position: RfqPosition;
  ctaLabel: string;
  // Explicit initial "By Type" filter (e.g. matching the vendor type of the
  // alternate whose "More alternatives" opened this). When provided it wins
  // over the position heuristic below.
  defaultTypeFilter?: ChipFilter;
  onPick: (product: Product) => void;
  onClose: () => void;
}

// The ranked candidate list with filter chips (All / Preferred Vendor /
// Private Label) replacing the old standalone "Private Label" tab.
const PicksTab = ({
  item,
  position,
  ctaLabel,
  defaultTypeFilter,
  onPick,
  onClose
}: PicksTabProps) => {
  const { productConfigurations } = useAppContext();

  // Client-configured display names for the vendor types (e.g. Sysco labels the
  // preferred-vendor slot "DON Branded" and the private-label slot "Preferred
  // Vendor"). Same source the "D"/"P" ProductLabel badges use; fall back to the
  // generic names when a client hasn't configured them.
  const pvLabel =
    productConfigurations?.strategicVendorConfig?.title || 'Preferred Vendor';
  const plLabel =
    productConfigurations?.privateLabelConfig?.title || 'Private Label';

  const candidates = useMemo(
    () =>
      (item?.matchedInternalProductsId || []).filter(
        (c: any) => c?.reasoning || c?.percentage
      ),
    [item?.matchedInternalProductsId]
  );

  const hasPV = candidates.some((c: any) => c?.isPreferredVendor);
  const hasPL = candidates.some((c: any) => c?.isPrivateLabel);

  // Initial "By Type" filter. An explicit request from the caller (matching the
  // clicked alternate's vendor type) is authoritative — falling back to "All"
  // when that chip isn't available. Otherwise use the position heuristic:
  // Alt 1 favors PV, Alt 2 favors PL, Top shows All.
  let defaultFilter: ChipFilter = 'all';
  if (defaultTypeFilter !== undefined) {
    if (defaultTypeFilter === 'pv' && hasPV) defaultFilter = 'pv';
    else if (defaultTypeFilter === 'pl' && hasPL) defaultFilter = 'pl';
  } else if (position === 'alt1' && hasPV) defaultFilter = 'pv';
  else if (position === 'alt2' && hasPL) defaultFilter = 'pl';
  const [filter, setFilter] = useState<ChipFilter>(defaultFilter);

  const filtered = useMemo(() => {
    if (filter === 'pv')
      return candidates.filter((c: any) => c?.isPreferredVendor);
    if (filter === 'pl')
      return candidates.filter((c: any) => c?.isPrivateLabel);
    return candidates;
  }, [candidates, filter]);

  const chips: { key: ChipFilter; label: string }[] = [
    { key: 'all', label: 'All Types' },
    ...(hasPV ? [{ key: 'pv' as ChipFilter, label: pvLabel }] : []),
    ...(hasPL ? [{ key: 'pl' as ChipFilter, label: plLabel }] : [])
  ];

  // Preselect the product occupying the position being edited — the Top match
  // for `top`, or the alt snapshot for Alt 1 / Alt 2. ProductMatch matches the
  // radio by `_id` (productKeyGetter); the alt snapshot stores the catalog id as
  // `productId`, so surface it as `_id`.
  const editedAlt =
    position === 'top'
      ? undefined
      : item?.alternates?.find(a => a.slot === (position === 'alt1' ? 1 : 2));
  let selected: Product | null =
    item?.matchedInternalProducts?.[item?.internalIndex || 0] || null;
  if (position !== 'top') {
    selected =
      editedAlt?.state === 'filled'
        ? ({ ...editedAlt, _id: editedAlt.productId } as unknown as Product)
        : null;
  }

  // "Type" filter select (All / Preferred Vendor / Private Label) shown next to
  // the built-in Manufacturer/Stock/Cost filters, replacing the old chip tabs.
  const activeTypeLabel =
    chips.find(c => c.key === filter)?.label || 'All Types';
  const typeFilter =
    chips.length > 1 ? (
      <ProductFilter
        anchorWidth={170}
        title={activeTypeLabel}
        label="By Type"
        fontColor={filter !== 'all' ? TextColor.Blue : undefined}
        clearAll={filter !== 'all' ? () => setFilter('all') : null}
        dropdownContent={
          <ListContainer>
            {chips.map(c => {
              const isSelected = filter === c.key;
              return (
                <ListItem key={c.key} onClick={() => setFilter(c.key)}>
                  <SText
                    title={c.label}
                    size="sm"
                    lineHeight="20px"
                    fontColor={isSelected ? TextColor.Blue : undefined}
                  />
                  {isSelected && <CheckIcon />}
                </ListItem>
              );
            })}
          </ListContainer>
        }
      />
    ) : null;

  return (
    <Box width="100%">
      <ProductMatch
        fetch
        ctaLabel={ctaLabel}
        onClose={onClose}
        preFilter={typeFilter}
        ids={filtered.map((c: any) => c?._id)}
        products={filtered as any}
        selected={selected as Product}
        setSelected={onPick}
      />
    </Box>
  );
};

export interface PositionsModalProps {
  item: RFQProducts;
  position: RfqPosition;
  isSingerUser?: boolean;
  // Preselects the "By Type" filter on the picks tab (used when opened from an
  // alternate's "More alternatives" to match that alternate's vendor type).
  defaultTypeFilter?: ChipFilter;
  // `picked` is the full catalog product selected (in-list / catalog tab), so
  // the caller can carry its image/catalog fields onto the new Top even though
  // the server's positions response returns it slim (QOT-1729). Absent for
  // manual entries (no catalog product).
  // `external` is true when an off-list pick (Catalog / Ask AI / Manual) lands
  // on Top, so the caller can restore the ExternalChip ("Manual Selection" /
  // "Manual Entry") the slim positions response drops.
  onApply: (body: PositionsBody, picked?: Product, external?: boolean) => void;
  onClose: () => void;
}

export const PositionsModal = ({
  item,
  position,
  isSingerUser,
  defaultTypeFilter,
  onApply,
  onClose
}: PositionsModalProps) => {
  const isTop = position === 'top';

  const apply = (
    pick: PositionValue,
    source: AlternatesReplaceSource,
    picked?: Product
  ) => {
    alternatesAnalytics.replace(position, source);
    // Off-list picks (Catalog / Ask AI / Manual) placed at Top must show the
    // ExternalChip. The positions endpoint returns the new Top without the
    // `external` flag the chip reads, so signal it here for the live-response
    // bridge in `adopt`; in-list AI picks keep their % badge instead.
    onApply(
      buildPositionsBody(item, position, pick),
      picked,
      isTop && source !== 'in-list'
    );
    onClose();
  };

  const matchedProduct =
    item?.matchedInternalProducts?.[item?.internalIndex || 0] || null;

  // Top card's "View more matches" sets the Top match; an alternate's controls
  // (swap icon / "More alternatives" link) both open the picker for that
  // alternate's own slot.
  const title = isTop
    ? 'View more matches'
    : `Replace ${position === 'alt1' ? 'Alternative 1' : 'Alternative 2'}`;
  const description = `${
    isTop ? 'More Matches' : 'More Alternatives'
  } for ${toTitleCase(item?.productName || item?.desc || '-')} ${
    item?.competitorSku ? `(SKU: ${item?.competitorSku})` : ''
  }`;

  return (
    <BaseDialog
      newDesign
      title={title}
      description={description}
      onClose={onClose}
      elements={[
        {
          content: (
            <PicksTab
              item={item}
              position={position}
              ctaLabel="Select Item"
              defaultTypeFilter={defaultTypeFilter}
              onClose={onClose}
              onPick={p => apply({ productId: p?._id }, 'in-list', p)}
            />
          ),
          name: 'Top AI Picks',
          key: Tabs.PRODUCT_MATCH
        },
        {
          content: (
            <Catalog
              product={matchedProduct as Product}
              onClose={onClose}
              // Catalog / Ask AI picks are off-list — the server stores them
              // with null % (rendered as "Manually selected").
              handleSubmit={(p: Product) =>
                apply({ productId: p?._id }, 'catalog', p)
              }
            />
          ),
          name: 'Catalog & Ask AI',
          key: Tabs.CATALOG
        },
        {
          content: (
            <ManualEntry
              product={matchedProduct as Product}
              isSingerUser={isSingerUser}
              onClose={onClose}
              handleSubmit={(props: ManualProps) =>
                apply(
                  {
                    manual: {
                      name: props?.name || '',
                      sku: props?.sku || '',
                      sell: Number(props?.price) || 0,
                      // Without this the server keeps the previous match's
                      // manufacturer / MFG SKU on the item and the new Top shows
                      // a stale, wrong MFG.
                      manufacturer: props?.manufacturer || ''
                    }
                  },
                  'manual'
                )
              }
            />
          ),
          name: 'Manual Entry',
          key: Tabs.MANUAL
        }
      ]}
    />
  );
};

export default PositionsModal;
